import range from "lodash/range";
import { jsx } from "panel";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { ResizablePanelEl } from "common/components/resizable-panel";
import { Direction, PanelName, Theme } from "common/components/resizable-panel/types";
import { handleKeyPress } from "common/util/focusable";
import { KEYDOWN_EVENTS } from "common/components/resizable-panel/constants";
import { SidenavItemEl } from "./sidenav-item";
import { NestingOrder } from "./types";
import { ScrollPosition, SIDENAV_DEFAULT_WIDTH, SIDENAV_MAX_WIDTH, SIDENAV_MIN_WIDTH } from "./constants";
function Loader() {
  return /* @__PURE__ */ jsx("div", { sel: ".loader", attrs: { elref: `sidenavLoader` } }, /* @__PURE__ */ jsx("div", { sel: ".mp-skeleton-header" }), range(9).map((_) => /* @__PURE__ */ jsx("div", { sel: ".mp-skeleton-large" })));
}
function SidenavItemWithNestedItems(props) {
  var _a, _b;
  const section = props.section;
  const item = props.item;
  const idx = props.idx;
  const sectionIdx = props.sectionIdx;
  const selectedItemValue = props.selectedItemValue;
  const hoveredItemValues = props.hoveredItemValues;
  const sectionDragDisabled = props.sectionDragDisabled;
  const activeDragSectionIdx = props.activeDragSectionIdx;
  const onItemDrop = props.onItemDrop;
  const onItemDragStart = props.onItemDragStart;
  const onItemDragEnd = props.onItemDragEnd;
  const onSelectItem = props.onSelectItem;
  const toggleItemChildren = props.toggleItemChildren;
  const onItemOverflowMenuChange = props.onItemOverflowMenuChange;
  const onItemOverflowMenuSelect = props.onItemOverflowMenuSelect;
  const onItemMouseEntered = props.onItemMouseEntered;
  const preventDefaultNavigate = props.preventDefaultNavigate;
  const isNestedDashboardsEnabled = props.isNestedDashboardsEnabled;
  const expandedItemSet = props.expandedItemSet;
  const deepLevel = props.deepLevel || 0;
  const parentItemId = props.parentItemId || ``;
  const nestedOrder = props.nestedOrder || NestingOrder.First;
  const itemId = parentItemId ? `${parentItemId}-${item.value}` : `${sectionIdx}-${item.value}`;
  const nestedItemsToShow = expandedItemSet.has(itemId) && ((_a = item.nestedItems) == null ? void 0 : _a.length) ? item.nestedItems : [];
  return /* @__PURE__ */ jsx("div", { sel: ".sidenav-item" }, /* @__PURE__ */ jsx(
    SidenavItemEl,
    {
      attrs: {
        ...selectedItemValue && { "is-selected": item.value === selectedItemValue },
        "is-new": item.isNew && !(hoveredItemValues == null ? void 0 : hoveredItemValues.has(item.value)),
        "is-draggable": section.dragDropEnabled,
        "is-drag-disabled": sectionDragDisabled,
        "can-drop-item": Number.isInteger(activeDragSectionIdx),
        "label-tab-size": deepLevel,
        "is-nested-dashboards-enabled": isNestedDashboardsEnabled,
        "can-expand": ((_b = item.nestedItems) == null ? void 0 : _b.length) > 0,
        "is-expanded": expandedItemSet.has(itemId),
        "nested-order": nestedOrder
      },
      on: {
        itemDrop: onItemDrop,
        itemDragStart: onItemDragStart,
        itemDragEnd: onItemDragEnd,
        itemClick: onSelectItem,
        itemExpand: toggleItemChildren,
        overflowMenuChange: onItemOverflowMenuChange,
        overflowMenuSelect: onItemOverflowMenuSelect,
        mouseenter: () => onItemMouseEntered(item)
      },
      props: { preventDefaultNavigate, item: { ...item, itemIdx: idx, sectionIdx, itemId } }
    }
  ), /* @__PURE__ */ jsx("div", { sel: ".sidenav-nested-items", class: { "is-selected": selectedItemValue && selectedItemValue === item.value } }, nestedItemsToShow.map((subItem, index) => /* @__PURE__ */ jsx(
    SidenavItemWithNestedItems,
    {
      section,
      item: subItem,
      sectionIdx,
      idx,
      selectedItemValue,
      hoveredItemValues,
      sectionDragDisabled,
      activeDragSectionIdx,
      onItemDrop,
      onItemDragStart,
      onItemDragEnd,
      onSelectItem,
      toggleItemChildren,
      onItemOverflowMenuChange,
      onItemOverflowMenuSelect,
      onItemMouseEntered,
      preventDefaultNavigate,
      deepLevel: deepLevel + 1,
      isNestedDashboardsEnabled,
      expandedItemSet,
      parentItemId: itemId,
      nestedOrder: index === item.nestedItems.length - 1 && (nestedOrder === NestingOrder.Last || nestedOrder === NestingOrder.First) ? NestingOrder.Last : NestingOrder.Middle
    }
  ))));
}
function SidenavCtaEl(props) {
  const cta = props.cta;
  const isExpanded = props.isExpanded;
  const isSelected = props.isSelected;
  const onClick = props.onClick;
  const preventDefaultNavigate = props.preventDefaultNavigate;
  const iconRenderer = (isCollapsed) => {
    return /* @__PURE__ */ jsx("div", { sel: ".icon-wrapper", class: { "is-selected": isSelected, collapsed: isCollapsed } }, /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: cta.icon } }));
  };
  if (isExpanded) {
    return /* @__PURE__ */ jsx(
      SidenavItemEl,
      {
        sel: ".cta-item",
        props: { preventDefaultNavigate, iconRenderer, item: cta },
        attrs: { "is-selected": isSelected },
        on: { itemClick: (ev) => onClick(ev.detail.value) }
      }
    );
  } else {
    return /* @__PURE__ */ jsx(
      "a",
      {
        sel: ".cta-item.collapsed",
        on: { click: () => onClick(cta.value), keypress: handleKeyPress },
        attrs: { value: cta.value, tabindex: 0, ...cta.href ? { href: cta.href } : {} }
      },
      iconRenderer(true),
      /* @__PURE__ */ jsx(TooltipEl, { attrs: { placement: Placement.Right } }, cta.label)
    );
  }
}
function SidenavSection(props, sectionItemsChildren) {
  const section = props.section;
  const sectionDragDisabled = props.sectionDragDisabled;
  const isExpanded = props.isExpanded;
  const onToggleSection = props.onToggleSection;
  const { print } = props.i18n;
  const isSectionCollapsible = section.label && section.isCollapsible;
  return /* @__PURE__ */ jsx("div", { sel: ".section", attrs: { elref: `sidenavSection` }, class: { "active-dragging-disabled": sectionDragDisabled } }, section.label && /* @__PURE__ */ jsx(
    "div",
    {
      sel: ".section-header",
      attrs: { elref: `sidenavSectionHeader`, tabindex: 0 },
      on: isSectionCollapsible && { click: onToggleSection, keypress: handleKeyPress },
      class: { "is-collapsible": isSectionCollapsible }
    },
    /* @__PURE__ */ jsx(SvgIconEl, { attrs: { icon: isExpanded ? `caret-down` : `caret-right` } }),
    /* @__PURE__ */ jsx("div", { sel: ".section-header-label" }, print(section.label))
  ), isExpanded && /* @__PURE__ */ jsx("div", { sel: ".section-items-list", attrs: { role: `list` } }, sectionItemsChildren));
}
export function template() {
  var _a;
  const sections = this.state.sections;
  const { print } = this.getContext(`i18n`);
  const selectedItemValue = this.helpers.getSelectedValue();
  const sidenavContent = this.attr(`loading`) ? /* @__PURE__ */ jsx(Loader, null) : sections.map((section, sectionIdx) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;
    const isExpanded = !this.state.collapsedSectionSet.has(section.key || print(section.label));
    if (section.items.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsx(
      SidenavSection,
      {
        section,
        sectionDragDisabled,
        isExpanded,
        onToggleSection: () => this.helpers.toggleSection(section),
        i18n: this.getContext(`i18n`)
      },
      isExpanded && section.items.filter(
        (item) => {
          var _a2;
          return !item.featureFlag || ((_a2 = this.getContext(`currentProject`).get().value) == null ? void 0 : _a2.hasFeatureFlag(item.featureFlag));
        }
      ).map((item, idx) => /* @__PURE__ */ jsx(
        "div",
        {
          sel: ".sidenav-item .sidenav-nested-items",
          class: {
            ...selectedItemValue && { "is-selected": selectedItemValue === item.value },
            "is-child-selected": selectedItemValue && this.isItemOrChildSelected(item, selectedItemValue)
          },
          attrs: {
            role: `listitem`,
            "aria-label": print(item.label),
            value: item.value
          },
          hook: {
            insert: this.helpers.handleSidenavItemInserted
          }
        },
        /* @__PURE__ */ jsx(
          SidenavItemWithNestedItems,
          {
            section,
            item,
            idx,
            sectionIdx,
            selectedItemValue,
            hoveredItemValues: this.state.hoveredItemValues,
            sectionDragDisabled,
            activeDragSectionIdx: this.state.activeDragSectionIdx,
            onItemDrop: this.helpers.onItemDrop,
            onItemDragStart: this.helpers.onItemDragStart,
            onItemDragEnd: this.helpers.onItemDragEnd,
            onSelectItem: this.helpers.onSelectItem,
            toggleItemChildren: this.helpers.toggleItemChildren,
            onItemOverflowMenuChange: this.helpers.onItemOverflowMenuChange,
            onItemOverflowMenuSelect: this.helpers.onItemOverflowMenuSelect,
            onItemMouseEntered: this.helpers.onItemMouseEntered,
            preventDefaultNavigate: this.preventDefaultNavigate,
            isNestedDashboardsEnabled: this.attr(`is-nested-dashboards-enabled`),
            expandedItemSet: this.state.expandedItemSet
          }
        )
      ))
    );
  });
  if (this.attr(`contents-only`)) {
    return /* @__PURE__ */ jsx("div", { sel: ".sidebar.content-only.top-border" }, /* @__PURE__ */ jsx("div", { sel: ".wrapper" }, /* @__PURE__ */ jsx(
      "div",
      {
        sel: ".sections-container",
        hook: { insert: this.helpers.onSectionsInserted },
        attrs: { elref: `sectionsContainer` }
      },
      /* @__PURE__ */ jsx("div", { sel: ".sections" }, sidenavContent)
    )));
  }
  const header = this.attr(`header`);
  return /* @__PURE__ */ jsx(
    ResizablePanelEl,
    {
      sel: ".sidebar",
      attrs: {
        "storage-key": this.attr(`storage-key`),
        "collapse-key": KEYDOWN_EVENTS.Backquote,
        "min-adjacent-size": this.attr(`min-adjacent-width`),
        "max-size": SIDENAV_MAX_WIDTH,
        "min-size": SIDENAV_MIN_WIDTH,
        "panel-name": PanelName.SideNav,
        "default-alignment": Direction.Left,
        "default-ratio": 0.15,
        "default-size": SIDENAV_DEFAULT_WIDTH,
        "fixed-size": this.attr(`fixed-size`),
        "disable-responsive-resize": true,
        "show-collapse-button": !(this.attr(`fixed-size`) || this.attr(`hide-collapse-button`)),
        theme: Theme.GlobalSidenav
      },
      on: {
        panelResize: (ev) => {
          const isCollapsed = ev.detail.isCollapsed;
          this.updateIsCollapsed(isCollapsed);
        }
      },
      props: { containerEl: this.parentElement }
    },
    /* @__PURE__ */ jsx(
      "div",
      {
        sel: ".wrapper",
        class: {
          collapsed: this.state.isCollapsed
        },
        attrs: { elref: `wrapper` }
      },
      header ? /* @__PURE__ */ jsx(
        "div",
        {
          sel: ".header",
          attrs: { elref: `sidenavHeader` },
          class: {
            "is-expanded-style": this.helpers.isExpandedStyle(),
            "bottom-border": ![ScrollPosition.Top, ScrollPosition.None].includes(this.state.scrollPosition) && this.helpers.isExpandedStyle(),
            "slot-header": `slot` in header,
            "actions-container": `actions` in header
          }
        },
        !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", { sel: ".title" }, header.title) : null,
        `slot` in header ? /* @__PURE__ */ jsx(
          "slot",
          {
            attrs: {
              name: this.state.isCollapsed && header.collapsedSlot ? header.collapsedSlot : header.slot
            }
          }
        ) : (_a = header.actions) == null ? void 0 : _a.map((cta) => /* @__PURE__ */ jsx(
          SidenavCtaEl,
          {
            cta,
            isExpanded: this.helpers.isExpandedStyle(),
            isSelected: this.attr(`selected`) === cta.value,
            onClick: this.helpers.onHeaderActionClick,
            preventDefaultNavigate: this.preventDefaultNavigate
          }
        ))
      ) : null,
      !this.state.isCollapsed ? /* @__PURE__ */ jsx(
        "div",
        {
          sel: ".sections-container",
          hook: { insert: this.helpers.onSectionsInserted },
          attrs: { elref: `sectionsContainer` }
        },
        /* @__PURE__ */ jsx("div", { sel: ".sections" }, sidenavContent)
      ) : null,
      !this.state.isCollapsed && this.footerCard
    )
  );
}
